'use client';
import { useAuth } from '@ahm/react-auth';
import { FloatingLabelInput, toast } from '@ahm/react-ui';
import { ActionIcon, Button, Card, Group, rem, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { usePathname } from 'next/navigation';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import SaveIcon from '@/assets/icons/save-icon.svg';
import ModalWrapper from '@/components/modals/modal-wrapper';
import { useCreateDraft } from '@/features/booking/api/order/use-create-draft';
import { useBookingRequestBodies } from '@/features/booking/hooks/use-booking-request';
import useIsMobile from '@/lib/is-mobile/use-is-mobile';
import { getServiceType } from '@/lib/utils';
import { useBookingStore } from '@/stores/booking/booking-store-provider';

interface SaveDraftFormData {
  draftName: string;
}

export default function SaveDraftSection() {
  const pathname = usePathname();
  const isMobile = useIsMobile();
  const [opened, { open, close }] = useDisclosure(false);
  const { createRequestBody } = useBookingRequestBodies(pathname, null);
  const { isTruckService } = useMemo(
    () => getServiceType(pathname),
    [pathname]
  );
  const { session } = useAuth();
  const user = session?.user;

  const { estimatedData } = useBookingStore((state) => state);
  const { handleSubmit, watch, reset, register } = useForm<SaveDraftFormData>({
    defaultValues: {
      draftName: '',
    },
  });

  const draftName = watch('draftName');

  const { mutate: createDraft, isPending } = useCreateDraft({
    onSuccess: (data) => {
      console.log('Draft created successfully:', data);
      reset();
      close();
      toast.success({
        title: 'Lưu đơn nháp thành công',
        withIcon: true,
        withCloseButton: false,
      });
    },
    onError: (error) => {
      console.error('Error creating draft:', error);
      toast.error({
        title: 'Lưu đơn nháp thất bại',
        withIcon: true,
        withCloseButton: false,
      });
    },
  });

  const handleSaveDraft = handleSubmit((data) => {
    if (!createRequestBody || !user || !estimatedData?.data) return;
    const draftData = {
      draft_order_note: data.draftName,
      type: 'SAVED_DRAFT',
      user_id: user?._id,
      total_pay: estimatedData?.data?.total_price,
      ...createRequestBody,
    };
    createDraft(draftData);
  });

  const handleClose = () => {
    reset();
    close();
  };

  if (!createRequestBody || !user || !estimatedData?.data || isTruckService)
    return null;

  return (
    <>
      <ActionIcon
        styles={(theme) => ({
          root: {
            boxShadow: isMobile ? 'unset' : theme.shadows.shadow8,
          },
        })}
        radius="50%"
        pos="absolute"
        top={isMobile ? 0 : 10}
        right={isMobile ? 6 : 16}
        size={42}
        variant="white"
      >
        <SaveIcon size={24} onClick={open} />
      </ActionIcon>

      <ModalWrapper
        optionStyle={{
          headerStyle: {
            minHeight: rem(56),
            borderBottom: `1px solid var(--mantine-color-neutral-1)`,
          },
          bodyStyle: {
            padding: 0,
          },
        }}
        title="Lưu đơn hàng nháp"
        isOpen={opened}
        onClose={handleClose}
      >
        <Card>
          <form onSubmit={handleSaveDraft}>
            <Text
              size={isMobile ? 'sm' : 'md'}
              fw={500}
              c="neutral.5"
              mb={rem(16)}
            >
              Bạn có thể đặt lại tên gọi dễ nhớ cho đơn nháp để dễ dàng quản lý
              các đơn nháp của mình hơn
            </Text>
            <FloatingLabelInput
              mb={isMobile ? 56 : 16}
              label={'Tên đơn nháp'}
              h="56px"
              maxLength={80}
              {...register('draftName')}
            />
            <Group
              p={isMobile ? 12 : 0}
              gap={8}
              pos={{ base: 'fixed', sm: 'sticky' }}
              bottom={0}
              left={0}
              w="100%"
              bg="white"
              styles={{
                root: {
                  zIndex: 100,
                },
              }}
              grow
            >
              <Button
                fullWidth
                c={'secondary.7'}
                variant={'light'}
                color={'neutral'}
                mih={rem(48)}
                radius={rem(4)}
                fw={700}
                onClick={handleClose}
                type="button"
              >
                Hủy
              </Button>
              <Button
                size="md"
                mih={rem(48)}
                w="50%"
                type="submit"
                disabled={!draftName || draftName.trim() === '' || isPending}
              >
                Lưu
              </Button>
            </Group>
          </form>
        </Card>
      </ModalWrapper>
    </>
  );
}
